import { ReactNode, useState } from "react";
import {
  ChevronRight,
  Headset,
  Info,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";

import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import { ButtonRebrand } from "@tudigo-monorepo/web-tudigo-components";

const VOTE_BUTTON_BASE_CLASSES = "flex h-[50px] w-[90px] rounded bg-white";
const VOTE_BUTTON_SELECTED = "border-primary-500 text-primary-500 border-[3px]";
const VOTE_BUTTON_UNSELECTED = "border-2 border-neutral-300";

interface VoteButtonProps {
  isSelected: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
}

const VoteButton = ({ isSelected, onClick, icon, label }: VoteButtonProps) => (
  <ButtonRebrand
    variant="ghost"
    className={cn(
      VOTE_BUTTON_BASE_CLASSES,
      isSelected ? VOTE_BUTTON_SELECTED : VOTE_BUTTON_UNSELECTED,
    )}
    onClick={onClick}
    aria-pressed={isSelected}
    aria-label={`Vote ${label.toLowerCase()}`}
  >
    <div
      className={cn("flex items-center gap-2", {
        "text-primary-500": isSelected,
        "text-black": !isSelected,
      })}
    >
      {icon}
      {label}
    </div>
  </ButtonRebrand>
);

export interface HelperVoterProps {
  className?: string;
  onVote?: (value: number) => void;
  onRequestSupport?: () => void;
}

export function HelperVoter({
  className,
  onVote,
  onRequestSupport,
}: HelperVoterProps) {
  const { t } = useTranslation();
  const [vote, setVote] = useState<boolean | null>(null);

  const handleVote = (isHelpful: boolean) => {
    setVote(isHelpful);
    onVote?.(Number(isHelpful));
  };

  return (
    <div
      className={cn("flex w-full flex-col items-center gap-4 px-6", className)}
      role="region"
      aria-label="Feedback section"
    >
      <div className="flex flex-col items-center gap-4 p-6">
        <h2 className="body300-regular text-neutral-900">
          {t("component.helper_voter.title")}
        </h2>
        <div className="flex gap-4">
          <VoteButton
            isSelected={vote === true}
            onClick={() => handleVote(true)}
            icon={<ThumbsUp className="h-5 w-5" />}
            label={t("component.helper_voter.button_yes")}
          />
          <VoteButton
            isSelected={vote === false}
            onClick={() => handleVote(false)}
            icon={<ThumbsDown className="h-5 w-5" />}
            label={t("component.helper_voter.button_no")}
          />
        </div>
      </div>

      {vote === true && (
        <div
          className="bg-primary-50 border-primary-200 flex h-[72px] w-full items-center gap-3 rounded-md border p-4"
          role="alert"
        >
          <Info className="text-primary-500 h-6 w-6" />
          <span className="body400-medium text-neutral-black">
            {t("component.helper_voter.feedback")}
          </span>
        </div>
      )}

      {vote === false && (
        <ButtonRebrand
          variant="ghost"
          className="hover:border-primary-500 flex h-[110px] w-full items-center justify-between rounded-lg border-neutral-300 bg-neutral-50 p-6"
          onClick={onRequestSupport}
          aria-label={t("component.helper_voter.contact_support")}
        >
          <div className="flex flex-col">
            <div className="mb-2 flex items-center gap-2">
              <Headset className="h-5 w-5" />
              <span className="body300-medium text-neutral-black">
                {t("component.helper_voter.request_support")}
              </span>
            </div>
            <span className="body200-regular text-start text-neutral-900">
              {t("component.helper_voter.contact_support")}
            </span>
          </div>
          <ChevronRight className="h-5 w-5" />
        </ButtonRebrand>
      )}
    </div>
  );
}
